import { Layouts } from '../../../controls/layouts';
import { Component, computed, input, ChangeDetectionStrategy, Signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Textbox } from '../../../controls/textbox';
import { CredentialsStatus, FormPartnerConversation, NewCommentEvent, PartnerOnboardingComment, PartnerOnboardingItemStatus, ToggleConversationEvent } from 'src/app/partnerportal/models/partner-onboarding';
import { OnboardingCommentsIconComponent } from 'src/app/partnerportal/components/onboarding-comments-icon/onboarding-comments-icon.component';
import { OnboardingCommentsContainerComponent } from 'src/app/partnerportal/components/onboarding-comments-container/onboarding-comments-container.component';
import { Conversation, ConversationMessage } from 'src/app/controls/components/advanced/conversation/models/conversation';
import { ConversationComponent } from 'src/app/controls/components/advanced/conversation/conversation.component';
import { OnboardingFile } from 'src/app/partnerportal/components/onboarding-file-selector/models/onboarding-file';
import { LinkComponent } from 'src/app/controls/components/basic/link/link.component';
import { Password, PasswordComponentValue, PasswordMode } from '../../../controls/password';
import { LayoutControlComponent } from 'src/app/controls/components/internal/layout-control/layout-control.component';
import { OnboardingCredentials } from 'src/app/partnerportal/components/onboarding-credentials/models/credentials';

@Component({
  selector: 'app-partner-onboarding-information-item',
  standalone: true,
  imports: [FormsModule, Layouts, LinkComponent, Password, Textbox, OnboardingCommentsIconComponent, OnboardingCommentsContainerComponent, ConversationComponent, LayoutControlComponent],
  templateUrl: './partner-onboarding-information-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './partner-onboarding-information-item.component.scss'
})
export class PartnerOnboardingInformationItemComponent {
    public item = input.required<InformationItem>();
    public conversationData = input<ConversationData | null>(null);

    secretMode: PasswordMode = PasswordMode.Secret;

    conversationToShow = computed(() => {
        if (!this.conversationData()) return null;
        return new Conversation(
            {
                Id: this.conversationData().visibleConversation()?.Id,
                AreaId: this.conversationData().visibleConversation()?.AreaId,
                UserId: 'customer',
                Comments: this.conversationData().visibleConversation()?.Comments?.map(cm => {
                    const userId = cm.IsAction ? 'system' : (cm.FromCustomer ? 'customer' : 'partner');
                    return new ConversationMessage({UserId: userId, Message: cm.Message, Timestamp: cm.Timestamp})
                }) ?? [],
                LastRead: this.conversationData().visibleConversation()?.PartnerLastRead
            }
        );
    });

    fileValue = computed(() => {
        if (this.item().itemType === 'file') {
            return this.item().itemValue as OnboardingFile;
        }
        return null;
    });

    passwordValue = computed(() => {
        if (this.item().itemType === 'password') {
            const pcv =  this.item().itemValue as PasswordComponentValue;
            return pcv;
        }
        return null;
    });

    credentialsValue = computed(() => {
        if (this.item().itemType === 'credentials') {
            return this.item().itemValue as OnboardingCredentials;
        }
        return null;
    });

    credentialsStatusValue = computed(() => {
        const credentialsItem = this.item() as CredentialsInformationItem;
        return credentialsItem?.credentialsStatus;
    });

    supportsProvideCredentialsValue = computed(() => {
        const credentialsItem = this.item() as CredentialsInformationItem;
        return credentialsItem?.supportsProvideCredentials;
    });

    isDisabled = computed(() => this.item()?.itemStatus?.Status.toLowerCase() === 'accepted');

    async provideCredentials(item: InformationItem): Promise<void> {
        if (item.itemType !== 'credentials') return;
        await item.provideCredentialsClicked(item);
    }

    hasUnreadMessages(conversationId: string): boolean {
        if (!this.conversationData()?.unreadConversationIds) {
          return false;
        }
        return this.conversationData()?.unreadConversationIds()?.some(id => id === conversationId);
    }

    downloadFile(onboardingFile: OnboardingFile) {
        if (!onboardingFile) return;
        this.downloadBase64File(onboardingFile.Data, onboardingFile.FileName, onboardingFile.Mime);
    }

    downloadBase64File(base64: string, filename: string, mime = 'application/octet-stream') {
        const parts = base64.split(',');
        const b64 = parts.length > 1 ? parts[1] : parts[0];

        const binary = atob(b64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

        const blob = new Blob([bytes], { type: mime });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();

        URL.revokeObjectURL(url);
    }

    async toggleConversation(conversationId: string, areaId: string | null): Promise<void> {
        await this.conversationData()?.toggleConversationClicked(new ToggleConversationEvent({ConversationId: conversationId, AreaId: areaId}));
    }

    async newComment(message: ConversationMessage, conversationId: string) {
        const comment = new PartnerOnboardingComment({FromCustomer: true, Message: message.Message, Timestamp: message.Timestamp});
        const newCommentEvent = new NewCommentEvent({Comment: comment, ConversationId: conversationId});
        await this.conversationData()?.newCommentClicked(newCommentEvent);
    }

    toggleAutoRefresh(): void {
        this.conversationData()?.toggleAutoRefreshClicked();
    }
}



export type InformationItemKind = 'text' | 'file' | 'password' | 'credentials';

export type InformationItemBase<TKind extends InformationItemKind> = {
    itemStatus: PartnerOnboardingItemStatus;
    itemName: string;
    itemType: TKind;
    partnerFilledItem: boolean;
};

export type TextInformationItem = InformationItemBase<'text'> & {
    itemValue: string;
    itemUpdated: (event: ItemUpdatedEvent) => Promise<void>;
};

export type FileInformationItem = InformationItemBase<'file'> & {
    itemValue: OnboardingFile;
    itemUpdated: (event: ItemUpdatedEvent) => Promise<void>;
};

export type PasswordInformationItem = InformationItemBase<'password'> & {
    itemValue: PasswordComponentValue;
    itemUpdated: (event: ItemUpdatedEvent) => Promise<void>;
};

export type CredentialsInformationItem = InformationItemBase<'credentials'> & {
    itemValue: OnboardingCredentials;
    credentialsStatus: CredentialsStatus | null;
    supportsProvideCredentials: boolean;
    provideCredentialsClicked: (item: CredentialsInformationItem) => Promise<void>;
};

export type InformationItem =
| TextInformationItem
| FileInformationItem
| PasswordInformationItem
| CredentialsInformationItem;

export type ConversationData = {
    conversationId: string | null;
    areaId: string;
    visibleConversation: Signal<FormPartnerConversation | null>;
    unreadConversationIds: Signal<string[]>;
    autoRefreshEnabled: Signal<boolean>;
    conversationContainerOffset: string;
    toggleAutoRefreshClicked: () => void;
    toggleConversationClicked: (event: ToggleConversationEvent) => Promise<void>;
    newCommentClicked: (event: NewCommentEvent) => Promise<void>;
};

export type ItemUpdatedEvent = {
    itemName: string;
    itemValue: string;
    itemType: string;
    itemStatus: PartnerOnboardingItemStatus;
}
